import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import api from '../api'
import { Send, Bot, User, Plus, Trash2, ShieldCheck, Cpu, Server } from 'lucide-react'
import { Card, PageHeader, Button, Input, Field, Alert, Select, Tabs, Badge } from '../components/ui'

function kosong(platformId) { return { id: null, label: '', model: '', platformId: platformId || '' } }

export default function AiAsisten() {
  // Tab (2026-07-23) -- dulu 3 panel pengaturan (API Key/Daftar Model/Kemampuan AI) ditumpuk vertikal
  // DI ATAS kartu chat, jadi chat selalu butuh scroll dulu utk kelihatan. Dipisah jadi tab spy tab
  // "AI Asisten" isinya cuma kartu chat sendirian -- pengaturan tetap ada, tinggal pindah tab, tidak
  // hilang. Default 'chat' krn itu yg paling sering dipakai stlh pengaturan awal selesai diisi.
  // Tab "API Key" DIHAPUS 2026-07-28 -- API key sekarang dikelola terpusat di halaman Platform AI
  // (tabel ai_platforms), tiap model di tab "Daftar Model" tinggal pilih platform mana yg dipakai.
  const [tab, setTab] = useState('chat')
  const [platforms, setPlatforms] = useState([])
  const [models, setModels] = useState([])
  const [tools, setTools] = useState([])
  const [settingsError, setSettingsError] = useState('')
  const [modelBaru, setModelBaru] = useState(kosong())

  const [activeModelId, setActiveModelId] = useState('')
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [chatError, setChatError] = useState('')
  const bottomRef = useRef(null)

  useEffect(() => { loadPengaturan() }, [])
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, sending])

  async function loadPengaturan() {
    const res = await api.get('/ai-asisten/pengaturan')
    setPlatforms(res.data.platforms || [])
    setModels(res.data.models || [])
    setTools(res.data.tools || [])
    if (res.data.models?.length && !activeModelId) setActiveModelId(res.data.models[0].id)
    setModelBaru(m => m.platformId ? m : kosong(res.data.platforms?.find(p => p.aktif)?.id))
  }

  async function toggleTool(id, enabled) {
    const res = await api.post('/ai-asisten/pengaturan', { tools: { [id]: enabled } })
    setTools(res.data.tools || [])
  }

  async function simpanModels(list) {
    setSettingsError('')
    try {
      const res = await api.post('/ai-asisten/pengaturan', { models: list })
      setModels(res.data.models || [])
      if (res.data.models?.length && !res.data.models.find(m => m.id === activeModelId)) {
        setActiveModelId(res.data.models[0].id)
      }
      return true
    } catch (e) {
      setSettingsError(e.response?.data?.message || 'Gagal menyimpan daftar model')
      return false
    }
  }

  async function tambahModel() {
    if (!modelBaru.platformId) { setSettingsError('Pilih platform dulu -- tambahkan di halaman Platform AI kalau belum ada.'); return }
    if (!modelBaru.model.trim()) { setSettingsError('Slug model wajib diisi'); return }
    const ok = await simpanModels([...models, { label: modelBaru.label, model: modelBaru.model.trim(), platformId: modelBaru.platformId }])
    if (ok) setModelBaru(kosong(modelBaru.platformId))
  }

  async function hapusModel(id) {
    await simpanModels(models.filter(m => m.id !== id))
  }

  async function kirim() {
    if (!input.trim() || sending) return
    if (!activeModelId) { setChatError('Tambahkan minimal 1 model dulu di tab Daftar Model.'); return }
    const next = [...messages, { role: 'user', content: input.trim() }]
    setMessages(next)
    setInput('')
    setChatError('')
    setSending(true)
    try {
      const res = await api.post('/ai-asisten/chat', { messages: next, modelId: activeModelId })
      setMessages([...next, { role: 'assistant', content: res.data.reply, toolsUsed: res.data.toolsUsed || [] }])
    } catch (e) {
      setChatError(e.response?.data?.message || 'Gagal mengirim pesan')
    } finally {
      setSending(false)
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); kirim() }
  }

  return (
    <div>
      <PageHeader
        title="AI Asisten"
        subtitle="Asisten AI internal — API key & provider dikelola di halaman Platform AI, izin baca data live aplikasi/Mikrotik diatur di tab Credential AI (nyala/mati per kemampuan, semua read-only)."
      />

      <Tabs
        tabs={[['chat', 'AI Asisten', Bot], ['kemampuan', 'Credential AI', ShieldCheck], ['model', 'Daftar Model', Cpu]]}
        active={tab} onChange={setTab}
      />

      {tab === 'model' && (
        <Card style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 4 }}>Daftar Model</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16 }}>
            Bisa lebih dari satu — tiap model dipilih per-percakapan lewat dropdown di kotak chat, dan terikat ke satu platform (API key-nya diatur di <Link to="/ai-platforms">Platform AI</Link>). Kalau providernya OpenRouter, salin slug dari <span style={{ fontFamily: 'monospace' }}>openrouter.ai/models</span> (cari yang berakhiran <span style={{ fontFamily: 'monospace' }}>:free</span> kalau mau gratis), atau isi <span style={{ fontFamily: 'monospace' }}>openrouter/free</span> supaya otomatis dipilihkan model gratis yang tersedia.
          </div>
          {settingsError && <Alert type="error">{settingsError}</Alert>}

          {platforms.length === 0 && (
            <Alert type="error">
              Belum ada platform AI. Tambahkan dulu di halaman <Link to="/ai-platforms">Platform AI</Link> (mis. OpenRouter) sebelum bisa menambah model di sini.
            </Alert>
          )}

          {models.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              {models.map(m => {
                const platform = platforms.find(p => p.id === m.platformId)
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ flex: '0 0 160px', fontWeight: 600, fontSize: 13 }}>{m.label}</div>
                    <div style={{ flex: 1, fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)', wordBreak: 'break-all' }}>{m.model}</div>
                    <Badge tone={platform?.aktif ? 'neutral' : 'danger'} style={{ flex: '0 0 auto' }}>
                      {platform ? platform.nama : 'Platform terhapus'}
                    </Badge>
                    <Button size="sm" variant="danger-soft" icon={Trash2} onClick={() => hapusModel(m.id)}>Hapus</Button>
                  </div>
                )
              })}
            </div>
          )}

          {platforms.length > 0 && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <Field label="Platform" style={{ flex: '0 0 160px' }}>
                <Select value={modelBaru.platformId} onChange={e => setModelBaru({ ...modelBaru, platformId: e.target.value })}>
                  {platforms.map(p => <option key={p.id} value={p.id} disabled={!p.aktif}>{p.nama}{!p.aktif ? ' (nonaktif)' : ''}</option>)}
                </Select>
              </Field>
              <Field label="Nama Tampilan" style={{ flex: '0 0 180px' }}>
                <Input value={modelBaru.label} onChange={e => setModelBaru({ ...modelBaru, label: e.target.value })} placeholder="mis. Model Coding" />
              </Field>
              <Field label="Slug Model" style={{ flex: 1 }}>
                <Input value={modelBaru.model} onChange={e => setModelBaru({ ...modelBaru, model: e.target.value })} placeholder="mis. cohere/north-mini-code:free" style={{ fontFamily: 'monospace', fontSize: 12.5 }} />
              </Field>
              <Button icon={Plus} onClick={tambahModel} style={{ flex: '0 0 auto' }}>Tambah</Button>
            </div>
          )}
        </Card>
      )}

      {tab === 'kemampuan' && (
        <Card style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14.5, marginBottom: 4 }}>Credential AI</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 16 }}>
            Kendalikan data apa yang boleh AI baca (semua read-only, tidak ada yang mengubah data). Kalau mati, AI akan jujur bilang belum punya izin & menuntun cara menyalakannya — tidak akan mengarang jawaban.
          </div>
          {tools.map(t => (
            <label key={t.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
              <input type="checkbox" checked={t.enabled} onChange={e => toggleTool(t.id, e.target.checked)} style={{ marginTop: 3 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13.5 }}>{t.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{t.description}</div>
              </div>
            </label>
          ))}
        </Card>
      )}

      {tab === 'chat' && (
      <Card style={{ padding: 0, display: 'flex', flexDirection: 'column', height: 620 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 600 }}>Model:</span>
          <Select value={activeModelId} onChange={e => setActiveModelId(e.target.value)} style={{ maxWidth: 320 }}>
            {models.length === 0 && <option value="">Belum ada model ditambahkan</option>}
            {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </Select>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {messages.length === 0 && (
            <div style={{ color: 'var(--text-faint)', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
              Belum ada percakapan. Coba tanya sesuatu untuk tes koneksi.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                background: m.role === 'user' ? 'var(--brand)' : 'var(--brand-soft)',
                color: m.role === 'user' ? '#fff' : 'var(--brand-soft-text)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {m.role === 'user' ? <User size={15} /> : <Bot size={15} />}
              </div>
              <div style={{ maxWidth: '75%' }}>
                <div style={{
                  padding: '10px 14px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.5,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: m.role === 'user' ? 'var(--brand)' : '#f2f2f5',
                  color: m.role === 'user' ? '#fff' : 'var(--text)'
                }}>
                  {m.content}
                </div>
                {m.toolsUsed?.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>
                    🔧 Pakai data: {m.toolsUsed.join(', ')}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                background: 'var(--brand-soft)', color: 'var(--brand-soft-text)',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}><Bot size={15} /></div>
              <div style={{ padding: '10px 14px', borderRadius: 12, fontSize: 13.5, background: '#f2f2f5', color: 'var(--text-muted)' }}>
                Mengetik…
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        {chatError && <div style={{ padding: '0 20px' }}><Alert type="error">{chatError}</Alert></div>}
        <div style={{ display: 'flex', gap: 10, padding: 16, borderTop: '1px solid var(--border)' }}>
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Tanya sesuatu untuk tes koneksi AI…"
            style={{ flex: 1 }}
          />
          <Button icon={Send} onClick={kirim} loading={sending} disabled={!input.trim()}>Kirim</Button>
        </div>
      </Card>
      )}
    </div>
  )
}
